import { computed, ComputedRef, Ref, ref, set, del } from 'vue';
import { castArray, difference, omit, cloneDeep } from 'lodash-es';
import api from '@/common/utils/api';
import mergeFn from '@/common/utils/merge';
import { DEFAULT_REQUEST_VIEW } from '@/common/store/modules/common/common.constants';
import cleanObjectProperties from '@/common/utils/cleanObjectProperties';
import useAccumulatedAction, { AccumulatedAction } from '@/common/store/pinia/common/useAccumulatedAction';
import { getNextMarker, isPageLast, applyPagingParams } from '@/common/store/pinia/common/paging';
import { formatArrayIn } from '@/common/utils/formatQueryParams';
import NotFoundError from '@/common/utils/errors/NotFoundError';
import CancellationError from '@/common/utils/errors/CancellationError';
import threadRequests from '@/common/utils/threadRequests';
import type { UnwrapRef } from 'vue';
import { takeLastByKey, abortByKey } from '@/common/utils/takeLastByKey';

export interface FetchOptions {
  includeId?: ID | ID[];
  marker?: ID;
  limit?: number;
  view?: string;
  params?: object;
  requestOptions?: object;
  abortController?: AbortController;
}

// @cpt-dod:cpt-pstorageuihci-dod-common-pinia-store-type-safe-params:p1
export type FetchItemsParams = Record<string, unknown>;

type ViewItem = {
  list: ID[];
  count: null | number;
  loading: boolean;
  marker: ID | undefined;
  isLastPage: boolean;
  requestParams: Record<string, any>;
  error: Error | null;
}

export type ItemsMap<TItem> = Ref<Record<ID, TItem>>
export type GetItemById<TMappedItem> = ComputedRef<(id?: ID) => TMappedItem | undefined>;
export type GetItems<TMappedItem> = ComputedRef<TMappedItem[]>;
export type GetItemsByView<TMappedItem> = ComputedRef<(view: string) => TMappedItem[]>;
export type GetItemByProp<TMappedItem> = ComputedRef<<K extends keyof TMappedItem>(prop: K) => (value: TMappedItem[K]) => TMappedItem | undefined>;
export type GetItemByProps<TMappedItem> = ComputedRef<(props: Partial<TMappedItem>) => TMappedItem | undefined>;
export type GetItemsByProp<TMappedItem> = ComputedRef<<K extends keyof TMappedItem>(prop: K) => (value: TMappedItem[K]) => TMappedItem[]>;
export type FindItemByProps<TMappedItem> = ComputedRef<({ index, ...props }: { index: number; [p: string]: any }) => TMappedItem | undefined>;
export type ViewLists = Ref<Record<string, ViewItem>>;

const createEmptyView = (): ViewItem => ({
  list: [],
  count: null,
  loading: false,
  marker: undefined,
  requestParams: {},
  isLastPage: false,
  error: null
});

export type CommonPiniaStore<TItem, TMappedItem> = {
  itemKey: keyof TItem;
  itemsMap: ItemsMap<TItem>;
  viewLists: ViewLists;
  allItems: ComputedRef<TMappedItem[]>;
  findItemByProps: FindItemByProps<TMappedItem>;
  getCount: ComputedRef<number|null>;
  getItemById: GetItemById<TMappedItem>;
  getItemByProp: GetItemByProp<TMappedItem>;
  getItemByProps: GetItemByProps<TMappedItem>;
  getItems: GetItems<TMappedItem>;
  getItemsByProp: GetItemsByProp<TMappedItem>;
  getItemsByView: GetItemsByView<TMappedItem>;
  getViewList: ComputedRef<(view?: string) => ViewItem | undefined>;
  addViewList: (view: string) => ViewItem;
  changeItem: (data: Partial<TItem>, addToList?: boolean, view?: string) => void;
  create: (body: Partial<TItem>, options?: object) => Promise<FetchResponse>;
  deleteView: (view: string) => void;
  fetchItem: (id: ID, options?: FetchOptions) => Promise<any>;
  fetchLastItem: (id: ID, options?: FetchOptions) => Promise<void>;
  abortFetchItem: (id: ID) => Promise<void>;
  abortAndRemoveItem: (id: ID) => Promise<void>;
  fetchItems: (payload?: FetchItemsParams, options?: FetchOptions) => Promise<any>;
  fetchAllItems: (payload?: FetchItemsParams, options?: FetchOptions) => Promise<void>;
  fetchItemsWithFilters: (id: ID | ID[], options?: FetchOptions) => Promise<any>;
  fetchItemsWithFiltersThrottled: AccumulatedAction;
  mergeItem: (data: Partial<TItem>) => void;
  pushData: (data: TItem[], view?: string) => void;
  remove: (id: ID, payload?: object) => Promise<any>;
  removeItem: (id: ID) => void;
  removeItems: (id: ID[]) => void;
  resetMarker: (view: string) => void;
  runCustomAction: (action: string, id: ID, payload?: object, method?: string) => Promise<any>
  setData: (data: TItem[], view?: string) => void;
  update: (id: ID, body: Partial<TItem>, event?: object) => Promise<any>;
  $reset: () => void;
  deleteItems: (ids: ID[], threadsCount?: number) => Promise<void[]>;
};

export type ExtractStoreItem<T> = T extends UnwrapRef<CommonPiniaStore<any, infer U>> ? U : never;

export type Options<TItem, TItemMapped> = {
  getUrl: () => Promise<string>|string;
  plural?: (arg: any) => TItem[],
  itemKey?: keyof TItem,
  mapItem?: (arg: TItem) => TItemMapped,
  updateMethod?: 'put' | 'patch',
  dataChangeMode?: 'direct' | 'task' | 'event'
  pagination?: 'none' | 'common' | 's3'
}

const FETCH_MARKER_LIMIT = 20;

export default function useCommonStore<TItem extends object, TMappedItem extends object>({
  getUrl,
  plural = ({ data }) => data,
  itemKey = 'id' as keyof TItem,
  mapItem = (item: TItem): TMappedItem => item as unknown as TMappedItem,
  updateMethod = 'patch',
  dataChangeMode = 'direct',
  pagination = 'none'
}: Options<TItem, TMappedItem>) {
  function commonStoreFn(): CommonPiniaStore<TItem, TMappedItem> {
    const getId = (item: Partial<TItem>) => item[itemKey] as ID;
    const itemsMap: ItemsMap<TItem> = ref({});
    const viewLists: ViewLists = ref({ [DEFAULT_REQUEST_VIEW]: createEmptyView() });
    const allItems = computed((): TMappedItem[] => {
      try {
        return Object.values(itemsMap.value).map((item) => mapItem(item));
      } catch {
        return [];
      }
    });

    const getItemById: GetItemById<TMappedItem> = computed(() => (id?: ID) => (id && id in itemsMap.value) ? mapItem(itemsMap.value[id]) : undefined);
    const getItems: GetItems<TMappedItem> = computed(() => viewLists.value[DEFAULT_REQUEST_VIEW].list
      .map((id: ID) => getItemById.value(id))
      .filter(Boolean) as TMappedItem[]
    );
    const getItemByProp: GetItemByProp<TMappedItem> = computed(() => (prop: keyof TMappedItem) => (value: TMappedItem[keyof TMappedItem]) => Object.values(allItems.value).find((item) => item[prop] === value));
    const getItemsByProp: GetItemsByProp<TMappedItem> = computed(() => (prop: keyof TMappedItem) => (value: TMappedItem[keyof TMappedItem]) => Object.values(allItems.value).filter((item) => item[prop] === value));
    const getItemByProps = computed(() => (props: Partial<TMappedItem>) => allItems.value.find(item => Object.keys(props).every(key => item[key] === props[key])));

    const getViewList = computed<(view?: string) => ViewItem | undefined>(() => (view: string = DEFAULT_REQUEST_VIEW) => Object.hasOwn(viewLists.value, view) ? viewLists.value[view] : undefined);

    const findItemByProps = computed(() => ({ index, ...props }: { index?: number; [p: string]: any }) => {
      if (index !== undefined) {
        return getItems.value[index];
      }
      return Object.values(allItems.value).find(item => Object.keys(props).every(key => item[key] === props[key]));
    });

    const getCount = computed(() => {
      if (pagination === 'none') {
        return getItems.value.length;
      }
      return viewLists.value[DEFAULT_REQUEST_VIEW].count;
    });

    const getItemsByView: GetItemsByView<TMappedItem> = computed(() => (view: string) => viewLists.value[view]?.list
      .map(id => getItemById.value(id))
      .filter(Boolean) as TMappedItem[] || []);

    const addViewList = (view: string) => {
      const viewList = getViewList.value(view);
      if (viewList) {
        return viewList;
      }
      set(viewLists.value, view, createEmptyView());
      return viewLists.value[view];
    };

    // @cpt-algo:cpt-pstorageuihci-algo-common-pinia-store-push-set-data:p1
    // @cpt-dod:cpt-pstorageuihci-dod-common-pinia-store-null-safe-count:p1
    const pushData = (data: TItem[], view = DEFAULT_REQUEST_VIEW) => {
      const viewList = addViewList(view);
      data.forEach((item) => {
        const id = getId(item);
        // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-push-set-data:p1:inst-upsert-map
        set(itemsMap.value, id, item);
        // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-push-set-data:p1:inst-upsert-map
        // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-push-set-data:p1:inst-append-list
        if (!viewList.list.includes(id)) {
          viewList.list.push(id);
          if (viewList.count !== null) {
            viewList.count += 1;
          }
        }
        // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-push-set-data:p1:inst-append-list
      });
    };

    // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-push-set-data:p1:inst-set-list
    const setData = (data: TItem[], view = DEFAULT_REQUEST_VIEW) => {
      const viewList = addViewList(view);

      viewList.list = data.map((item) => getId(item));
      data.forEach((item) => {
        set(itemsMap.value, getId(item), item);
      });
    };
    // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-push-set-data:p1:inst-set-list

    // @cpt-algo:cpt-pstorageuihci-algo-common-pinia-store-change-merge-item:p1
    const changeItem = (item: Partial<TItem>, addToList = true, view = DEFAULT_REQUEST_VIEW) => {
      const id = getId(item);
      // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-change-merge-item:p1:inst-deep-merge
      const mergedItem = mergeFn(cloneDeep(itemsMap.value[id]) || {}, item);
      // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-change-merge-item:p1:inst-deep-merge
      // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-change-merge-item:p1:inst-add-to-list
      if (addToList) {
        pushData([mergedItem as TItem], view);
        return;
      }
      set(itemsMap.value, id, mergedItem);
      // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-change-merge-item:p1:inst-add-to-list
    };

    // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-change-merge-item:p1:inst-shallow-merge
    const mergeItem = (item: Partial<TItem>) => {
      const id = getId(item);
      if (id && id in itemsMap.value) {
        set(itemsMap.value, id, mergeFn(itemsMap.value[id], item));
      }
    };
    // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-change-merge-item:p1:inst-shallow-merge

    const removeItem = (id: ID) => {
      Object.values(viewLists.value).forEach((viewList) => {
        if (viewList.list.includes(id)) {
          viewList.list = viewList.list.filter(it => it !== id);
          if (viewList.count !== null) {
            viewList.count -= 1;
          }
        }
      });
      del(itemsMap.value, id);
    };

    const removeItems = (ids: ID[]) => {
      const idsSet = new Set(ids);
      Object.values(viewLists.value).forEach((viewList) => {
        const removedCount = viewList.list.filter(it => idsSet.has(it)).length;
        viewList.list = viewList.list.filter(it => !idsSet.has(it));
        if (viewList.count !== null && removedCount > 0) {
          viewList.count -= removedCount;
        }
      });
      ids.forEach((id) => {
        del(itemsMap.value, id);
      });
    };

    // @cpt-algo:cpt-pstorageuihci-algo-common-pinia-store-fetch-items:p1
    // @cpt-state:cpt-pstorageuihci-state-common-pinia-store-view-item:p1
    const fetchItems = async (payload: FetchItemsParams = {}, options: FetchOptions = {}) => {
      const {
        includeId,
        view = DEFAULT_REQUEST_VIEW,
        marker,
        limit = FETCH_MARKER_LIMIT,
        ...requestOptions
      } = options;

      const includeIds = castArray(includeId);
      // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-fetch-items:p1:inst-resolve-view
      const viewList = addViewList(view);
      // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-fetch-items:p1:inst-resolve-view
      // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-fetch-items:p1:inst-clean-params
      let requestParams: FetchItemsParams = cleanObjectProperties(payload, true) as FetchItemsParams;
      // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-fetch-items:p1:inst-clean-params

      // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-fetch-items:p1:inst-apply-paging
      if (pagination !== 'none') {
        viewList.marker = marker;
        requestParams = applyPagingParams[pagination]?.(requestParams, {
          marker,
          limit
        });
      }
      // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-fetch-items:p1:inst-apply-paging

      // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-fetch-items:p1:inst-set-loading
      // @cpt-begin:cpt-pstorageuihci-state-common-pinia-store-view-item:p1:inst-to-loading
      // @cpt-begin:cpt-pstorageuihci-state-common-pinia-store-view-item:p1:inst-retry
      viewList.loading = true;
      viewList.error = null;
      // @cpt-end:cpt-pstorageuihci-state-common-pinia-store-view-item:p1:inst-retry
      // @cpt-end:cpt-pstorageuihci-state-common-pinia-store-view-item:p1:inst-to-loading
      // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-fetch-items:p1:inst-set-loading

      try {
        // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-fetch-items:p1:inst-get-items
        const url = await getUrl();
        const response = await api.get<TItem[]>(url, requestParams, requestOptions);
        // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-fetch-items:p1:inst-get-items
        // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-fetch-items:p1:inst-extract-items
        const rawItems = plural(response.data);
        // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-fetch-items:p1:inst-extract-items

        // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-fetch-items:p1:inst-paginate-branch
        if (pagination !== 'none') {
          // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-fetch-items:p1:inst-push-or-set
          if (viewList.marker) {
            pushData(rawItems, view);
          } else {
            setData(rawItems, view);
          }
          // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-fetch-items:p1:inst-push-or-set
          // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-fetch-items:p1:inst-store-params
          viewList.requestParams = cleanObjectProperties(payload, true);
          // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-fetch-items:p1:inst-store-params

          // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-fetch-items:p1:inst-next-marker
          const nextMarker = getNextMarker[pagination](response);
          // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-fetch-items:p1:inst-next-marker
          // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-fetch-items:p1:inst-is-last
          const isLastPage = isPageLast[pagination](response, nextMarker, rawItems, limit);
          // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-fetch-items:p1:inst-is-last

          // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-fetch-items:p1:inst-update-marker
          viewList.isLastPage = isLastPage;

          viewList.marker = isLastPage ? undefined : nextMarker;
          // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-fetch-items:p1:inst-update-marker
        } else {
          // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-fetch-items:p1:inst-set-no-pagination
          setData(rawItems, view);
          viewList.isLastPage = true;
          // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-fetch-items:p1:inst-set-no-pagination
        }
        // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-fetch-items:p1:inst-paginate-branch

        // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-fetch-items:p1:inst-update-count
        viewList.count = response.data.count;
        // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-fetch-items:p1:inst-update-count

        // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-fetch-items:p1:inst-include-ids
        const newIds = includeIds.filter(id => !viewList.list.includes(id) && getItemById.value(id));
        if (newIds.length) {
          viewList.list = viewList.list.concat(newIds);
          if (viewList.count !== null) {
            viewList.count += newIds.length;
          }
        }
        // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-fetch-items:p1:inst-include-ids

        // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-fetch-items:p1:inst-return
        // @cpt-begin:cpt-pstorageuihci-state-common-pinia-store-view-item:p1:inst-to-loaded
        return response;
        // @cpt-end:cpt-pstorageuihci-state-common-pinia-store-view-item:p1:inst-to-loaded
        // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-fetch-items:p1:inst-return
      } catch (error) {
        // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-fetch-items:p1:inst-catch
        // @cpt-begin:cpt-pstorageuihci-state-common-pinia-store-view-item:p1:inst-to-error
        if (!(error instanceof CancellationError)) {
          set(viewLists.value, view, createEmptyView());
          viewLists.value[view].error = error as Error;
        }
        // @cpt-end:cpt-pstorageuihci-state-common-pinia-store-view-item:p1:inst-to-error
        throw error;
        // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-fetch-items:p1:inst-catch
      } finally {
        // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-fetch-items:p1:inst-finally
        // @cpt-begin:cpt-pstorageuihci-state-common-pinia-store-view-item:p1:inst-to-idle
        viewList.loading = false;
        // @cpt-end:cpt-pstorageuihci-state-common-pinia-store-view-item:p1:inst-to-idle
        // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-fetch-items:p1:inst-finally
      }
    };

    // @cpt-algo:cpt-pstorageuihci-algo-common-pinia-store-fetch-all-items:p1
    const fetchAllItems = async (payload: FetchItemsParams = {}, options: FetchOptions = {}) => {
      // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-fetch-all-items:p1:inst-none-delegate
      if (pagination === 'none') {
        await fetchItems(payload, options);
        return;
      }
      // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-fetch-all-items:p1:inst-none-delegate

      const { view = DEFAULT_REQUEST_VIEW, limit = FETCH_MARKER_LIMIT } = options;
      // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-fetch-all-items:p1:inst-reset-marker
      const viewList = addViewList(view);

      viewList.marker = undefined;
      // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-fetch-all-items:p1:inst-reset-marker

      // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-fetch-all-items:p1:inst-loop
      do {
        // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-fetch-all-items:p1:inst-fetch-page
        // eslint-disable-next-line no-await-in-loop
        await fetchItems(payload, {
          ...options,
          view,
          limit,
          marker: viewList.marker
        });
        // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-fetch-all-items:p1:inst-fetch-page
      } while (!viewList.isLastPage);
      // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-fetch-all-items:p1:inst-loop
    };

    // @cpt-algo:cpt-pstorageuihci-algo-common-pinia-store-fetch-item:p1
    const fetchItem = async (id: ID, options: FetchOptions = {}) => {
      const {
        view = DEFAULT_REQUEST_VIEW,
        params,
        ...requestOptions
      } = options;

      try {
        // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-fetch-item:p1:inst-get
        const response = await api.get<TItem>(`${await getUrl()}/${id}`, params, requestOptions);
        // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-fetch-item:p1:inst-get
        // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-fetch-item:p1:inst-push
        pushData([response.data], view);
        // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-fetch-item:p1:inst-push
        // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-fetch-item:p1:inst-return
        return response;
        // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-fetch-item:p1:inst-return
      } catch (error) {
        // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-fetch-item:p1:inst-catch-404
        if (error instanceof NotFoundError) {
          removeItems([id]);
        }
        throw error;
        // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-fetch-item:p1:inst-catch-404
      }
    };

    const fetchLastItem = async (id: ID, options: FetchOptions = {}) => {
      const url = await getUrl();
      takeLastByKey(abortController => fetchItem(id, { ...options, abortController }), `${url}/${id}`);
    };

    const abortFetchItem = async (id: ID) => {
      const url = await getUrl();
      abortByKey(`${url}/${id}`);
    };

    const abortAndRemoveItem = async (id: ID) => {
      await abortFetchItem(id);
      removeItem(id);
    };

    // @cpt-algo:cpt-pstorageuihci-algo-common-pinia-store-fetch-items-with-filters:p1
    const fetchItemsWithFilters = async (id: ID | ID[], options: FetchOptions = {}) => {
      const {
        view = DEFAULT_REQUEST_VIEW,
        ...requestOptions
      } = options;

      // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-fetch-items-with-filters:p1:inst-resolve-params
      const viewList = addViewList(view);
      const params = omit(viewList.requestParams, ['sort']);
      const ids = castArray(id);
      // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-fetch-items-with-filters:p1:inst-resolve-params

      try {
        // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-fetch-items-with-filters:p1:inst-get-filtered
        const url = await getUrl();
        const response = await api.get<TItem[]>(url, {
          id: formatArrayIn(ids),
          ...params,
        }, requestOptions);
        // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-fetch-items-with-filters:p1:inst-get-filtered

        // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-fetch-items-with-filters:p1:inst-extract
        const rawItems = plural(response.data);
        const itemIds = rawItems.map(getId);
        // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-fetch-items-with-filters:p1:inst-extract
        // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-fetch-items-with-filters:p1:inst-remove-missing
        const itemsToRemove = difference(ids, itemIds);

        if (itemsToRemove.length) {
          viewList.list = viewList.list.filter(it => !itemsToRemove.includes(it));
        }
        // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-fetch-items-with-filters:p1:inst-remove-missing

        // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-fetch-items-with-filters:p1:inst-push-items
        if (rawItems.length) {
          pushData(rawItems, view);
        }
        // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-fetch-items-with-filters:p1:inst-push-items

        // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-fetch-items-with-filters:p1:inst-return
        return response;
        // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-fetch-items-with-filters:p1:inst-return
      } catch (error) {
        // @cpt-begin:cpt-pstorageuihci-algo-common-pinia-store-fetch-items-with-filters:p1:inst-catch
        viewLists.value[view].error = error as Error;
        throw error;
        // @cpt-end:cpt-pstorageuihci-algo-common-pinia-store-fetch-items-with-filters:p1:inst-catch
      }
    };

    const fetchItemsWithFiltersThrottled = useAccumulatedAction<FetchResponse<TItem[]>>(fetchItemsWithFilters);

    const create = async (body: Partial<TItem>, options?: object) => {
      const result = await api.post<TItem>(await getUrl(), body, options);
      if (dataChangeMode === 'direct') {
        changeItem(result.data);
      }
      return result;
    };

    const update = async (id: ID, body: Partial<TItem>, event?: object) => {
      const result = await api[updateMethod]<TItem>(`${await getUrl()}/${id}`, {
        ...body,
        event
      });
      if (dataChangeMode === 'direct') {
        return mergeItem(result.data);
      }
      return result;
    };

    const remove = async (id: ID, payload?: object) => {
      const result = await api.delete<TItem>(`${await getUrl()}/${id}`, payload);
      if (dataChangeMode === 'direct') {
        removeItem(id);
      }
      return result;
    };

    const deleteView = (view: string) => {
      del(viewLists.value, view);
    };

    const runCustomAction = async (action: string,
                                   id: ID,
                                   payload?: object,
                                   method: string = 'post'
    ) => api[method](`${await getUrl()}/${id}/${action}`, payload);

    const resetMarker = (view: string) => {
      if (viewLists.value[view]) {
        set(viewLists.value[view], 'marker', undefined);
      }
    };

    const $reset = () => {
      itemsMap.value = {};
      viewLists.value = { [DEFAULT_REQUEST_VIEW]: createEmptyView() };
    };

    const deleteItems = (ids: ID[], threadsCount = 5) => {
      const tasks = ids.map((id) => () => remove(id));
      return threadRequests(tasks, threadsCount);
    };

    return {
      // state
      itemKey,
      itemsMap,
      viewLists,
      // getters
      allItems,
      findItemByProps,
      getCount,
      getItemById,
      getItemByProp,
      getItemByProps,
      getItems,
      getItemsByProp,
      getItemsByView,
      getViewList,
      // actions
      addViewList,
      changeItem,
      create,
      deleteView,
      abortFetchItem,
      abortAndRemoveItem,
      fetchItem,
      fetchLastItem,
      fetchItems,
      fetchAllItems,
      fetchItemsWithFilters,
      fetchItemsWithFiltersThrottled,
      mergeItem,
      pushData,
      remove,
      removeItem,
      removeItems,
      resetMarker,
      runCustomAction,
      setData,
      update,
      $reset,
      deleteItems
    };
  }

  return commonStoreFn;
}
